import {
  Component,
  effect,
  forwardRef,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { NgClass } from '@angular/common';
import { PrimitiveTypes } from '@angular/cli/src/analytics/analytics-parameters';
import { OptionSwitcherItem } from '../../../interfaces/option-switcher.interface';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TooltipDirective } from '../../../directives/tooltip.directive';
import { replacePreTags } from '../../../utils/text.utils';

type OptionSwitcherValue = PrimitiveTypes | null;

@Component({
  selector: 'app-option-switcher',
  templateUrl: './option-switcher.component.html',
  imports: [NgClass, TooltipDirective],
  styleUrl: './option-switcher.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OptionSwitcherComponent),
      multi: true
    }
  ]
})
export class OptionSwitcherComponent implements ControlValueAccessor {
  options: InputSignal<OptionSwitcherItem[]> =
    input.required<OptionSwitcherItem[]>();

  change: OutputEmitterRef<PrimitiveTypes> = output<PrimitiveTypes>();

  // State
  disabled: InputSignal<boolean> = input<boolean>(false);
  selectedOptionId: WritableSignal<OptionSwitcherValue> =
    signal<OptionSwitcherValue>(null);
  isDisabled: WritableSignal<boolean> = signal<boolean>(false);

  constructor() {
    effect(() => {
      const selectedOptionId = this.selectedOptionId();
      const selectedOption = this.options().find(
        (option) => option.id === selectedOptionId
      );

      if (
        selectedOptionId == null ||
        (selectedOption != null && !selectedOption.disabled)
      ) {
        return;
      }

      this.selectedOptionId.set(null);
      this.onChange?.(null);
    });
  }

  onSelect(optionId: PrimitiveTypes): void {
    const option = this.options().find((item) => item.id === optionId);

    if (
      this.disabled() ||
      this.isDisabled() ||
      option == null ||
      option.disabled
    ) {
      return;
    }

    this.selectedOptionId.set(optionId);
    this.onChange?.(optionId);
    this.change.emit(optionId);
    this.onTouch?.();
  }

  isIcomoonIcon(rawIcon: string): boolean {
    return rawIcon.split(' ').some((icon) => icon.startsWith('icon-'));
  }

  processDescription(description: string | undefined): string {
    if (!description) {
      return '';
    }
    return replacePreTags(description);
  }

  // ControlValueAccessor
  private onChange?: (value: OptionSwitcherValue) => void;
  private onTouch?: () => void;

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(outsideValue: OptionSwitcherValue): void {
    this.selectedOptionId.set(outsideValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled.set(isDisabled);
  }
}
